import { defineStore } from 'pinia'
import { QueueAPI, type AgentCampaign, type QueueEntry } from '@/shared/api/queue'
import { useActiveCallStore } from './active_call'
import { useAgentStatusStore } from './agent_status'
import { useToasts } from './toasts'

/**
 * Session de numérotation automatique sur UNE campagne (« Mes campagnes »
 * dans la file d'appels agent).
 *
 * Cycle : start(campagne) → next() pioche le numéro côté serveur, le
 * sélectionne dans le softphone et déclenche l'appel automatiquement
 * (via `autoCallToken`). À la clôture de la fiche de fin d'appel, le
 * DispositionModal appelle `onCallCompleted()` qui enchaîne sur le numéro
 * suivant — immédiatement, ou après le compte à rebours de post-appel si
 * la campagne configure `wrap_up_seconds`.
 */
export const useDialerSessionStore = defineStore('dialer_session', {
  state: () => ({
    campaign: null as AgentCampaign | null,
    /** Session en cours — l'enchaînement automatique est actif. */
    running: false,
    /** Numéro actuellement en main dans la session. */
    current: null as QueueEntry | null,
    /** Numéros pending restants dans la campagne (info serveur). */
    remaining: 0,
    /** Appels traités (disposition saisie) depuis le début de la session. */
    calledCount: 0,
    fetchingNext: false,
    /** Secondes restantes du compte à rebours post-appel (0 = aucun). */
    countdown: 0,
    _countdownTimer: null as ReturnType<typeof setInterval> | null,
  }),

  getters: {
    isActive: (s) => s.running && !!s.campaign,
  },

  actions: {
    async start(campaign: AgentCampaign) {
      const agent = useAgentStatusStore()
      const toasts = useToasts()
      if (agent.current === 'offline') {
        toasts.warning(
          'Vous êtes hors ligne',
          'Passez en « Disponible » pour démarrer une session d\'appels.',
        )
        return
      }
      const call = useActiveCallStore()
      if (call.isActive) {
        toasts.warning('Appel en cours', 'Terminez l\'appel avant de démarrer une session.')
        return
      }
      this.campaign = campaign
      this.running = true
      this.calledCount = 0
      this.remaining = campaign.available
      await this.next()
    },

    /** Pioche et lance le prochain numéro. Annule tout compte à rebours. */
    async next() {
      if (!this.campaign || this.fetchingNext) return
      // Garde-fou : ne JAMAIS enchaîner tant que l'appel précédent n'est pas
      // clôturé — appel encore actif OU fiche de fin d'appel ouverte
      // (disposition pas encore saisie). Sinon on composait le numéro suivant
      // et on consommait une fiche serveur alors que l'agent n'avait ni statué
      // ni clôturé le précédent. Le chaînage reprend via `onCallCompleted()` à
      // la validation de la disposition. (Même garde que
      // `active_call.dialAndCall` pour les appels manuels.)
      if (useActiveCallStore().isBusy) return
      this.cancelCountdown()
      const toasts = useToasts()
      this.fetchingNext = true
      try {
        const res = await QueueAPI.nextInCampaign(this.campaign.id)
        this.remaining = res.remaining
        if (!res.entry) {
          toasts.success(
            'Campagne terminée',
            `Tous les numéros de « ${this.campaign.name} » ont été traités. Bravo !`,
          )
          this.stop()
          return
        }
        this.current = res.entry
        const call = useActiveCallStore()
        call.selectFromQueue(res.entry)
        // Déclenche la composition automatique (PhonePanel observe ce token).
        call.autoCallToken++
      } catch (e: any) {
        const code = e?.response?.data?.error
        if (code === 'campaign_not_callable') {
          toasts.warning(
            'Hors plage horaire',
            'Cette campagne n\'est pas appelable en ce moment.',
          )
        } else {
          toasts.error('Numéro suivant indisponible', 'Réessayez dans quelques instants.')
        }
        this.stop()
      } finally {
        this.fetchingNext = false
      }
    },

    /**
     * Appelé par le DispositionModal après l'enregistrement de la fiche de
     * fin d'appel. Enchaîne automatiquement : tout de suite, ou après le
     * compte à rebours `wrap_up_seconds` de la campagne (l'agent peut
     * cliquer « Appeler maintenant » pour court-circuiter).
     */
    onCallCompleted() {
      if (!this.isActive) return
      this.calledCount++
      this.current = null
      const wrap = this.campaign?.wrap_up_seconds || 0
      if (wrap > 0) {
        this.startCountdown(wrap)
      } else {
        void this.next()
      }
    },

    startCountdown(seconds: number) {
      this.cancelCountdown()
      this.countdown = seconds
      this._countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          this.cancelCountdown()
          void this.next()
        }
      }, 1000)
    },

    cancelCountdown() {
      if (this._countdownTimer) {
        clearInterval(this._countdownTimer)
        this._countdownTimer = null
      }
      this.countdown = 0
    },

    stop() {
      this.cancelCountdown()
      this.running = false
      this.campaign = null
      this.current = null
      this.fetchingNext = false
    },
  },
})
